import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * AvatarContext — one shared cache of {email → avatar_url, name} so every
 * OwnerAvatar in the tree can render without its own fetch. Refreshed after
 * the current user changes their photo.
 */
const AvatarContext = createContext(null);

export function AvatarProvider({ children }) {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const refresh = useCallback(async () => {
        if (!user) {
            setUsers([]);
            setLoaded(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/avatars");
            setUsers(data.users || []);
        } catch (_e) {
            /* silent — falls back to initials-only rendering */
        } finally {
            setLoaded(true);
        }
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    /** Resolve a task's `owner` string (name or email) → {name, avatar_url, initials, color}. */
    const resolveOwner = useCallback(
        (owner) => {
            if (!owner) return null;
            const trimmed = String(owner).trim();
            if (!trimmed) return null;

            const lower = trimmed.toLowerCase();
            const isEmail = lower.includes("@");
            let match = null;
            if (isEmail) {
                match = users.find((u) => u.email === lower);
            } else {
                // Try full-name exact match first, then first-name match
                match = users.find((u) => (u.name || "").toLowerCase() === lower);
                if (!match) {
                    const first = lower.split(" ")[0];
                    match = users.find(
                        (u) => (u.name || "").toLowerCase().split(" ")[0] === first
                    );
                }
                if (!match) {
                    // Try matching the local part of an email (e.g., "joshr")
                    match = users.find(
                        (u) => (u.email || "").split("@")[0] === lower.replace(/\s+/g, "")
                    );
                }
            }

            const displayName = match?.name || (isEmail ? trimmed.split("@")[0] : trimmed);
            return {
                name: displayName,
                email: match?.email || (isEmail ? lower : null),
                avatar_url: match?.avatar_url || null,
                initials: computeInitials(displayName),
                color: colorFromString(match?.email || displayName),
            };
        },
        [users]
    );

    const value = useMemo(
        () => ({ users, loaded, refresh, resolveOwner }),
        [users, loaded, refresh, resolveOwner]
    );

    return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
}

export function useAvatars() {
    const ctx = useContext(AvatarContext);
    if (!ctx) throw new Error("useAvatars must be used inside <AvatarProvider>");
    return ctx;
}

// -----------------------------------------------------------------------------
// Deterministic initials + color helpers
// -----------------------------------------------------------------------------

export function computeInitials(name) {
    if (!name) return "?";
    const parts = String(name)
        .replace(/@.*$/, "") // strip email domain if given
        .replace(/[._-]+/g, " ")
        .trim()
        .split(/\s+/);
    if (parts.length === 0) return "?";
    if (parts.length === 1) {
        const p = parts[0];
        return (p.slice(0, 2) || p.slice(0, 1) || "?").toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Small palette that reads well against the dark VIRTUS theme.
const AVATAR_PALETTE = [
    "#FF5500", // virtus orange
    "#00E676", // green
    "#00B0FF", // blue
    "#FFEA00", // yellow
    "#A278FF", // purple
    "#FF7A00", // amber
    "#FF1744", // red
    "#4CD964", // lime
    "#DF3760", // pink
    "#7A4CD9", // dark purple
];

export function colorFromString(str) {
    if (!str) return AVATAR_PALETTE[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}
